import NotFound from "@/components/Common/NotFound";
import type {TokenClaimsSerializer} from "@/generated/DjangoTypes";
import {i18n} from "@/i18n";
import {selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import useTokenStore from "@/store/token";
import {fetchCompany} from "@/utils/loaderUtils";
import {createFileRoute, notFound, redirect} from "@tanstack/react-router";
import {getDefaultStore} from "jotai";
import {jwtDecode} from "jwt-decode";

export const Route = createFileRoute("/_authenticated/media-library")({
  notFoundComponent: () => (
    <NotFound message={i18n.t("errors:company_not_found")} page={i18n.t("common:dashboard")} redirect="/" />
  ),
  loader: async () => {
    const token = useTokenStore.getState().access;
    if (!token) {
      throw redirect({to: "/login", replace: true});
    }

    const claims = jwtDecode<TokenClaimsSerializer>(token);
    const selectedCompanyId = getDefaultStore().get(selectedCompanyIdAtom);
    const companyId = selectedCompanyId ?? claims.sub_brand_ids?.[0];

    if (!companyId) {
      const agencyId = claims.agency_ids?.[0];

      if (!agencyId) {
        throw redirect({to: "/", replace: true});
      }

      throw redirect({to: "/agency/$agencyId/media-library", params: {agencyId}, replace: true});
    }

    const isAdmin = claims.access_level === "COMPANY_ADMIN" || claims.access_level === "INFLUENCER_ADMIN";
    const isAgency = claims.access_level === "AGENCY";

    if (!isAdmin && !isAgency) {
      throw redirect({to: "/company/$companyId/media-library", params: {companyId}, replace: true});
    }

    const company = await fetchCompany(companyId).catch(() => null);

    if (!company) {
      throw notFound();
    }

    if (company.agency?.multiple_brands) {
      throw redirect({
        to: "/agency/$agencyId/subbrand/$companyId/media-library",
        params: {agencyId: company.agency.id, companyId: company.id},
        replace: true,
      });
    }

    throw redirect({
      to: "/company/$companyId/media-library",
      params: {companyId},
      replace: true,
    });
  },
});
